import PrivateReportImage from './PrivateReportImage'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  listOwnProblemReports,
  getOwnProblemReport,
} from '../../api/generated/problem-reports/problem-reports'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { formatDateTime } from '../../lib/datetime'
import { useProblemReportState } from '../../lib/problem-reporting/host'
import { reportQueryKey } from '../../lib/problem-reporting/context'
import { reportCopy, reportEventLabel, reportClosureLabel } from './copy'
import { useReportRead } from './own-data'
import type { createOwnConfirmation } from './confirmation'
import { reportInputClass } from './ReportForm'
export function OwnReports({ onSelect }: { onSelect: (id: string) => void }) {
  const t = useT(reportCopy)
  const [cursor, setCursor] = useState<string | undefined>()
  const query = useReportRead(['own', cursor], (options) =>
    listOwnProblemReports({ cursor, limit: 10 }, options),
  )
  if (query.isPending) return <p role="status">{t.loading}</p>
  if (query.isError || query.data?.status !== 200)
    return (
      <div role="alert">
        {t.error}
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t.retry}
        </Button>
      </div>
    )
  const page = query.data.data
  return (
    <div className="space-y-4">
      {!page.items.length && (
        <div className="py-10 text-center">
          <h3 className="font-semibold">{t.empty}</h3>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            {t.emptyHelp}
          </p>
        </div>
      )}
      {page.items.map((report) => (
        <button
          key={report.id}
          onClick={() => onSelect(report.id)}
          className="block w-full space-y-1 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3 text-left text-sm focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
        >
          <span className="flex justify-between gap-2 font-medium">
            <span>{report.reference}</span>
            <span>{t[report.status]}</span>
          </span>
          <span className="block text-xs text-[var(--color-text-muted)]">
            {t[report.kind]}
          </span>
          <span className="line-clamp-2 break-words">{report.description}</span>
          <span className="block text-xs text-[var(--color-text-muted)]">
            {t.submitted}: {formatDateTime(report.created_at)}
            <br />
            {t.updated}: {formatDateTime(report.updated_at)}
          </span>
        </button>
      ))}
      <div className="flex justify-between">
        {cursor && (
          <Button variant="ghost" onClick={() => setCursor(undefined)}>
            {t.back}
          </Button>
        )}
        {page.has_more && page.next_cursor && (
          <Button
            variant="outline"
            onClick={() => setCursor(page.next_cursor!)}
          >
            {t.next}
          </Button>
        )}
      </div>
    </div>
  )
}
export function OwnReportDetail({
  id,
  confirmation,
  onBack,
}: {
  id: string
  confirmation: ReturnType<typeof createOwnConfirmation>
  onBack: () => void
}) {
  const t = useT(reportCopy)
  const host = useProblemReportState()
  const client = useQueryClient()
  const state = useSyncExternalStore(
    confirmation.subscribe,
    confirmation.getState,
    confirmation.getState,
  )
  const query = useReportRead(['detail', id], (options) =>
    getOwnProblemReport(id, options),
  )
  const outcome = state.id === id ? state.outcome : 'idle'
  useEffect(() => {
    if (query.data?.status === 200)
      confirmation.reconcile(id, query.data.data.revision)
  }, [confirmation, id, query.data])
  useEffect(() => {
    if (outcome === 'saved' || outcome === 'conflict')
      void client.invalidateQueries({ queryKey: reportQueryKey() })
  }, [client, outcome])
  const frozen = outcome === 'pending' || outcome === 'unknown'
  const otherFrozen =
    state.id !== id && ['pending', 'unknown'].includes(state.outcome)
  if (query.isPending) return <p role="status">{t.loading}</p>
  if (query.isError || query.data?.status !== 200)
    return (
      <div>
        <Button variant="ghost" onClick={onBack}>
          {t.back}
        </Button>
        <p role="alert">{t.unavailableTarget}</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t.retry}
        </Button>
      </div>
    )
  const report = query.data.data
  return (
    <div className="space-y-4 text-sm">
      <Button variant="ghost" size="sm" onClick={onBack}>
        {t.back}
      </Button>
      <div className="flex justify-between gap-3 font-medium">
        <h3>{report.reference}</h3>
        <span>{t[report.status]}</span>
      </div>
      <p className="whitespace-pre-wrap break-words">{report.description}</p>
      {report.attachments.map((file) => (
        <PrivateReportImage key={file.id} id={id} fileId={file.id} audience="own" mimeType={file.mime_type} />
      ))}
      {(report.resolution_note || report.closure_reason) && (
        <section className="space-y-2 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3">
          <h4 className="font-medium">{t.resolution}</h4>
          <p className="whitespace-pre-wrap break-words">
            {report.resolution_note}
          </p>
          {report.fixed_release && (
            <p>
              {t.release}: {report.fixed_release}
            </p>
          )}
          {report.closure_reason && <p>{reportClosureLabel(report.closure_reason, t)}</p>}
        </section>
      )}
      {(report.status === 'fixed' || frozen) && (
        <section className="space-y-2">
          {outcome === 'conflict' && <p role="alert">{t.conflict}</p>}
          {state.id === id && state.error && outcome !== 'conflict' && (
            <p role="alert">
              {state.error.code === 'invalid_description' ? t.invalid : t.error}
            </p>
          )}
          {frozen ? (
            <>
              <p role="status">{t.pending}</p>
              <Button
                disabled={outcome === 'pending'}
                onClick={() =>
                  void confirmation.send(id, report.revision, 'fixed')
                }
              >
                {t.recover}
              </Button>
            </>
          ) : (
            <>
              <Button
                disabled={
                  otherFrozen ||
                  query.isFetching ||
                  outcome === 'saved' ||
                  !host.policy?.enabled ||
                  !host.policy.can_self
                }
                onClick={() =>
                  void confirmation.send(id, report.revision, 'fixed')
                }
              >
                {t.confirm}
              </Button>
              <label className="block" htmlFor="report-explanation">
                {t.explanation}
              </label>
              <textarea
                id="report-explanation"
                className={reportInputClass}
                value={state.id === id ? state.explanation : ''}
                onChange={(event) => confirmation.edit(id, event.target.value)}
                disabled={
                  otherFrozen ||
                  query.isFetching ||
                  outcome === 'saved' ||
                  !host.policy?.enabled ||
                  !host.policy.can_self
                }
              />
              <Button
                variant="outline"
                disabled={
                  otherFrozen ||
                  query.isFetching ||
                  outcome === 'saved' ||
                  !host.policy?.enabled ||
                  !host.policy.can_self
                }
                onClick={() =>
                  void confirmation.send(id, report.revision, 'still_broken')
                }
              >
                {t.reopen}
              </Button>
            </>
          )}
        </section>
      )}
      {outcome === 'saved' && <p role="status">{t.confirmed}</p>}
      <section>
        <h4 className="mb-2 font-medium">{t.history}</h4>
        <ol className="space-y-3">
          {report.events.map((event) => (
            <li
              key={event.id}
              className="border-l border-[var(--color-border)] pl-3"
            >
              <p>
                {reportEventLabel(event, t, report.attachments.some(file => file.mime_type.startsWith('video/')))}
              </p>
              {event.public_note && (
                <p className="whitespace-pre-wrap break-words">
                  {event.public_note}
                </p>
              )}
              <time className="text-xs text-[var(--color-text-muted)]">
                {formatDateTime(event.created_at)}
              </time>
            </li>
          ))}
        </ol>
      </section>
    </div>
  )
}
